import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";
import { extractPasswordManagerError, getPasswordManagerAuthHeaders, passwordManagerUrl } from "./personaApi";
import { ICompanyPersona } from "./resetAutomationTypes";

export interface IGetCompanyPersonaParams {
	companyId: number;
}

interface ICompanyPersonaState {
	data: ICompanyPersona | null;
	loading: boolean;
	error: string | null;
}

const initialState: ICompanyPersonaState = {
	data: null,
	loading: false,
	error: null,
};

export const getCompanyPersona = createAsyncThunk<ICompanyPersona | null, IGetCompanyPersonaParams>(
	"passwordManager/getCompanyPersona",
	async (params, { rejectWithValue, dispatch }) => {
		try {
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.get<ICompanyPersona>(passwordManagerUrl(`/Companies/${params.companyId}/persona`), {
					headers: getPasswordManagerAuthHeaders(),
				}),
			);
			return res.data ?? null;
		} catch (error: unknown) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return null;
			}
			return rejectWithValue(extractPasswordManagerError(error, "Failed to load company persona"));
		}
	},
);

const companyPersonaSlice = createSlice({
	name: "passwordManager/companyPersona",
	initialState,
	reducers: {
		resetCompanyPersona: (state) => {
			state.data = null;
			state.error = null;
			state.loading = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getCompanyPersona.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getCompanyPersona.fulfilled, (state, action) => {
				state.loading = false;
				state.data = action.payload;
			})
			.addCase(getCompanyPersona.rejected, (state, action) => {
				state.loading = false;
				state.data = null;
				state.error = (action.payload as string) || action.error.message || "Failed to load company persona";
			});
	},
});

export const { resetCompanyPersona } = companyPersonaSlice.actions;
export const companyPersonaReducer = companyPersonaSlice.reducer;
